// Plain-language wording for the Add Correction dialog.
//
// Users do not think in signed quantities ("-1") — they think "I sold one" or
// "we received six". A correction is therefore expressed as a DIRECTION (a
// verb phrase such as "Sold" / "Returned by customer") plus an unsigned
// COUNT; the signed stored value is derived here, never typed.
//
// Direction is read from the SIGN of the effective amount, not from the
// stored action, because the two disagree in real data (a return line inside
// a sale is `subtract` with a positive qty; a credit line on a vendor invoice
// is `add` with a negative qty). Negative raw = stock leaves the location /
// money comes in from the customer; positive raw = the reverse.
//
// Pure functions, no Angular — see correction-wording.spec.ts.

export interface CorrectionDirection {
  // -1: raw amount negative; 1: raw amount positive.
  sign: -1 | 1;
  // Verb phrase shown in the dropdown and in sentences, e.g. "Sold".
  label: string;
}

export interface CorrectionLedgerInfo {
  key?: string;
  type?: string;
  name?: string;
  actions?: { add?: { name?: string }; subtract?: { name?: string } };
  amount?: { prefix?: string; suffix?: string; precision?: number; divisor?: number };
}

export interface CorrectionWording {
  // Inventory corrections talk about units at a location; money corrections
  // talk about amounts on a tender.
  kind: 'inventory' | 'money';
  // What the record is about: the item name or the tender name.
  subject: string;
  // Where the stock or money is recorded.
  where: string | null;
  // The two directions, ordered [negative raw, positive raw].
  directions: [CorrectionDirection, CorrectionDirection];
  // Context shown after the statement, e.g. "Vendor to Store VS-1234".
  context: string | null;
  // Money shown in the register's orientation (a payment is positive, a refund
  // negative) — true for tenders and gift cards. Other ledgers (tax, tips,
  // points) keep their stored sign: positive means more.
  inverse: boolean;
  // Amount formatting (money); inventory counts are plain numbers.
  prefix: string;
  suffix: string;
  precision: number;
}

// Purchase types that move stock between two locations: the transaction's
// locationId is the side whose balance changed and customerName is the other.
const TRANSFER_TYPES = ['retail-retail', 'warehouse-retail', 'retail-warehouse', 'store-store'];

// Vendor receipts and returns: customerName is the vendor.
const VENDOR_IN_TYPES = ['vendor-retail', 'vendor-warehouse'];
const VENDOR_OUT_TYPES = ['retail-vendor'];

// Sales and customer returns at the register.
const SALE_TYPES = ['retail-customer', 'customer-retail'];

// Adjustment flows: one location, no counterparty. Singular names read well
// in "Breakage removed 2 units".
const ADJUSTMENT_NAMES: { [key: string]: string } = {
  'adjustment-retail': 'Adjustment',
  'retail-adjustment': 'Write-off',
  breakage: 'Breakage',
  tasters: 'Tasters',
  theft: 'Theft',
  donations: 'Donation',
  'physical-count': 'Physical count',
  'cyclic-count': 'Cyclic count',
  'inventory-correction': 'Inventory correction',
  'case-break': 'Case break',
};

// Payment tenders the customer hands over at the register.
const TENDER_TYPES = ['cash', 'credit', 'check'];

const TENDER_NAMES: { [key: string]: string } = {
  cash: 'Cash',
  credit: 'Credit Card',
  check: 'Check',
  gift: 'Gift Card',
  tax: 'Tax',
  tip: 'Tip',
  discount: 'Discount',
  loyalty: 'Loyalty',
  resource: 'Resource',
};

const clean = (value: any): string => (value == null ? '' : String(value).trim());

const capitalize = (value: string): string => value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Direction pair for an inventory transaction, keyed on its purchase type.
export function inventoryDirections(transaction: any): [CorrectionDirection, CorrectionDirection] {
  const purchaseType = clean(transaction?.purchaseType);
  const other = clean(transaction?.customerName);

  if (!purchaseType || SALE_TYPES.includes(purchaseType)) {
    return [{ sign: -1, label: 'Sold' }, { sign: 1, label: 'Returned by customer' }];
  }
  if (VENDOR_IN_TYPES.includes(purchaseType)) {
    const vendor = other || 'vendor';
    return [{ sign: -1, label: 'Returned to ' + vendor }, { sign: 1, label: 'Received from ' + vendor }];
  }
  if (VENDOR_OUT_TYPES.includes(purchaseType)) {
    const vendor = other || 'vendor';
    return [{ sign: -1, label: 'Returned to ' + vendor }, { sign: 1, label: 'Received back from ' + vendor }];
  }
  if (TRANSFER_TYPES.includes(purchaseType)) {
    // A leg against the transit location names the far end of the transfer
    // instead ("Sent to 29-North Anthony", not "Sent to In Transit").
    const transit = /^in transit$/i.test(other);
    const to = (transit && clean(transaction?.destinationName)) || other || 'the other location';
    const from = (transit && clean(transaction?.originName)) || other || 'the other location';
    return [{ sign: -1, label: 'Sent to ' + to }, { sign: 1, label: 'Received from ' + from }];
  }
  const name = ADJUSTMENT_NAMES[purchaseType] || capitalize(purchaseType.replace(/-/g, ' '));
  return [{ sign: -1, label: name + ' removed' }, { sign: 1, label: name + ' added' }];
}

// Direction pair for a money transaction. Register tenders and gift cards get
// customer-facing phrases; every other ledger uses its own action names
// (Charge / Refund for tax, Collected / Returned for tips, ...).
export function moneyDirections(transaction: any, ledger?: CorrectionLedgerInfo): [CorrectionDirection, CorrectionDirection] {
  const type = clean(transaction?.type);
  if (TENDER_TYPES.includes(type)) {
    return [{ sign: -1, label: 'Customer paid' }, { sign: 1, label: 'Refunded to customer' }];
  }
  if (type === 'gift') {
    return [{ sign: -1, label: 'Redeemed from gift card' }, { sign: 1, label: 'Loaded onto gift card' }];
  }
  const subtract = clean(ledger?.actions?.subtract?.name);
  const add = clean(ledger?.actions?.add?.name);
  return [
    { sign: -1, label: subtract || 'Subtracted' },
    { sign: 1, label: add || 'Added' },
  ];
}

// Human name of the purchase type for the context line.
export function purchaseTypeName(purchaseType: string): string | null {
  const typeKey = clean(purchaseType);
  if (!typeKey || SALE_TYPES.includes(typeKey)) { return null; }
  const names: { [k: string]: string } = {
    'retail-retail': 'Store to Store',
    'warehouse-retail': 'Warehouse to Store',
    'retail-warehouse': 'Store to Warehouse',
    'store-store': 'Store to Store',
    'vendor-retail': 'Vendor to Store',
    'vendor-warehouse': 'Vendor to Warehouse',
    'retail-vendor': 'Return to Vendor',
  };
  return names[typeKey] || ADJUSTMENT_NAMES[typeKey] || capitalize(typeKey.replace(/-/g, ' '));
}

export function buildWording(transaction: any, ledger?: CorrectionLedgerInfo): CorrectionWording {
  const type = clean(transaction?.type);
  const where = clean(transaction?.locationName) || null;

  if (type === 'inventory') {
    const typeName = purchaseTypeName(transaction?.purchaseType);
    const purchaseKey = clean(transaction?.purchaseKey);
    return {
      kind: 'inventory',
      subject: clean(transaction?.name) || clean(transaction?.accountName) || 'this item',
      where,
      directions: inventoryDirections(transaction),
      context: typeName
        ? (typeName + (purchaseKey ? ' ' + purchaseKey : ''))
        : (purchaseKey ? 'Purchase ' + purchaseKey : null),
      inverse: false,
      prefix: '',
      suffix: '',
      precision: 0,
    };
  }

  const tender = clean(ledger?.name) || TENDER_NAMES[type] || capitalize(type) || 'this ledger';
  const amount = ledger?.amount || {};
  const key = clean(transaction?.purchaseKey);
  // Points, uses and credits are whole numbers with no currency sign.
  const unitless = type === 'loyalty' || type === 'discount' || type === 'resource';
  return {
    kind: 'money',
    subject: tender,
    where,
    directions: moneyDirections(transaction, ledger),
    context: key ? 'Purchase ' + key : null,
    inverse: TENDER_TYPES.includes(type) || type === 'gift',
    prefix: amount.prefix != null ? clean(amount.prefix) : (unitless ? '' : '$'),
    suffix: amount.suffix != null ? clean(amount.suffix) : '',
    precision: typeof amount.precision === 'number' ? amount.precision : (unitless ? 0 : 2),
  };
}

// Which of the two directions a raw amount is in. Zero (a count that changed
// nothing) is shown as the negative-side verb so the default reads naturally
// ("Sold 0 units") and the user flips it if they mean the other way.
export function directionFor(wording: CorrectionWording, raw: number): CorrectionDirection {
  return raw > 0 ? wording.directions[1] : wording.directions[0];
}

// "1 unit" / "6 units" / "$13.90" / "150 points".
export function formatQuantity(wording: CorrectionWording, magnitude: number): string {
  const value = Math.abs(magnitude || 0);
  if (wording.kind === 'inventory') {
    const text = Number.isInteger(value) ? String(value) : String(Math.round(value * 10000) / 10000);
    return text + (value === 1 ? ' unit' : ' units');
  }
  return wording.prefix + value.toFixed(wording.precision) + wording.suffix;
}

// "Sold 1 unit" / "Customer paid $13.90" / "Physical count added 0 units".
export function statement(wording: CorrectionWording, raw: number, divisor: number): string {
  const magnitude = Math.abs(raw || 0) / (divisor || 1);
  return directionFor(wording, raw).label + ' ' + formatQuantity(wording, magnitude);
}

// What the correction does to the balance, in words the user can check
// against the shelf or the drawer. deltaRaw is target - effective in raw
// stored units.
export function effectSentence(wording: CorrectionWording, deltaRaw: number, divisor: number): string {
  if (!deltaRaw) { return ''; }
  const magnitude = formatQuantity(wording, Math.abs(deltaRaw) / (divisor || 1));
  const place = wording.where ? ' at ' + wording.where : '';
  if (wording.kind === 'inventory') {
    // Negative raw delta = more stock leaves.
    return 'On hand' + place + ' goes ' + (deltaRaw < 0 ? 'down' : 'up') + ' by ' + magnitude + '.';
  }
  // Tenders: negative raw delta = more money in from the customer. Other
  // ledgers read their stored sign directly.
  const up = wording.inverse ? deltaRaw < 0 : deltaRaw > 0;
  return wording.subject + ' recorded' + place + ' goes ' + (up ? 'up' : 'down') + ' by ' + magnitude + '.';
}

// The signed correction entry the server will write, for the small print.
export function entryText(wording: CorrectionWording, deltaRaw: number, divisor: number): string {
  if (!deltaRaw) { return ''; }
  const magnitude = Math.abs(deltaRaw) / (divisor || 1);
  if (wording.kind === 'inventory') {
    return (deltaRaw < 0 ? '−' : '+') + formatQuantity(wording, magnitude);
  }
  // Tenders follow the transaction panel's inverse orientation: a payment is
  // shown positive, so a raw negative delta is "+". Other ledgers keep their
  // stored sign.
  const positive = wording.inverse ? deltaRaw < 0 : deltaRaw > 0;
  return (positive ? '+' : '−') + formatQuantity(wording, magnitude);
}
